import { Button } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';

import { CardModal } from 'components/card-modal';
import LoadingAnimation from 'components/loading-animation';
import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Typography } from 'components/ui';
import { useLoyaltyHowItWorks } from 'hooks/use-loyalty-how-it-works';
import { primitive, spacing } from 'styles/constants';
import { blockContentToPlainText } from 'utils/sanity';

const FEATURE_LOYALTY_HOW_IT_WORKS_ID = 'feature-loyalty-how-it-works-ui-singleton';

const STEP_CONTENT_NUMBER_SIZE = 24;

export default function RewardsHowItWorksRoute() {
  const router = useRouter();
  const {
    loading,
    pageTitle,
    callToActionText,
    steps,
    modalHeader,
    pageSubtitle,
  } = useLoyaltyHowItWorks(FEATURE_LOYALTY_HOW_IT_WORKS_ID);

  const handleOnDismiss = () => router.dismiss();

  if (loading) {
    return (
      <View style={styles.loadingContainer} accessibilityRole="progressbar">
        <LoadingAnimation />
      </View>
    );
  }

  return (
    <CardModal
      dismissType="close"
      footer={
        <Button
          width="full"
          onPress={handleOnDismiss}
          accessibilityLabel={callToActionText}
          accessibilityRole="button"
        >
          {callToActionText}
        </Button>
      }
      scrollView
      headerText={modalHeader}
    >
      <View focusable accessibilityLabel={`${pageTitle} steps`} style={styles.mainContainer}>
        <View style={styles.titleContainer}>
          <Typography style={styles.title}>{pageTitle}</Typography>
          <SanityBlockRenderer content={pageSubtitle} textProps={{ style: styles.subtitle }} />
        </View>
        <View style={styles.stepsContainer}>
          {steps.map((step, index) => {
            if (!step) {
              return null;
            }
            const stepImage = step.stepImage?.locale;
            const stepImageAlt = stepImage?.asset?.altText || `Step ${index + 1} illustration`;
            const stepTitle = step.stepTitle?.locale || '';
            const stepDescription = step.stepDescription?.localeRaw
              ? blockContentToPlainText(step.stepDescription.localeRaw)
              : '';
            const isLastStep = index === steps.length - 1;

            return (
              <View key={step._key}>
                <View
                  style={styles.stepImageContainer}
                  accessibilityRole="image"
                  accessibilityLabel={stepImageAlt}
                  accessibilityHint={`Illustration for step ${index + 1}`}
                  focusable
                >
                  {stepImage && (
                    <Picture
                      width="100%"
                      image={stepImage}
                      alt={stepImageAlt}
                      objectFitContain
                      maxWidth={343}
                    />
                  )}
                </View>
                <View
                  style={[
                    styles.stepContentContainer,
                    isLastStep && {
                      borderBottomWidth: 0,
                    },
                  ]}
                  focusable
                >
                  <View style={styles.stepContentTitleContainer}>
                    <View
                      style={styles.stepContentNumberContainer}
                      accessibilityRole="text"
                      accessibilityLabel={`Step ${index + 1} of ${steps.length}`}
                      accessible
                      focusable
                    >
                      <Typography style={styles.stepContentNumber}>{index + 1}</Typography>
                    </View>
                    <View
                      focusable
                      accessibilityRole="header"
                      accessibilityLevel={2}
                      accessible
                      accessibilityLabel={`Step ${index + 1} title: ${stepTitle}`}
                    >
                      <Typography style={styles.stepContentTitle}>{stepTitle}</Typography>
                    </View>
                  </View>
                  <View style={styles.stepContentDescriptionContainer}>
                    <View
                      focusable
                      accessibilityRole="text"
                      accessible
                      accessibilityLabel={`Step ${index + 1} description: ${stepDescription}`}
                    >
                      <Typography style={styles.stepContentDescription}>
                        {stepDescription}
                      </Typography>
                    </View>
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      </View>
    </CardModal>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: primitive.$white,
  },
  mainContainer: {
    gap: spacing.$8,
  },
  titleContainer: {
    gap: spacing.$1,
  },
  title: {
    fontSize: primitive.fontSizes['2xl'],
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
    textAlign: 'center',
    marginTop: spacing.$4,
  },
  subtitle: {
    fontSize: primitive.fontSizes.sm,
    color: primitive.$bbqBrown,
    textAlign: 'center',
    paddingHorizontal: spacing.$6,
  },
  stepsContainer: {
    gap: spacing.$6,
  },
  stepImageContainer: {
    alignItems: 'center',
  },
  stepContentContainer: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingVertical: spacing.$6,
    marginHorizontal: spacing.$6,
    borderBottomWidth: 1,
    borderColor: primitive.$bbqBrown,
    borderStyle: 'solid',
    gap: spacing.$2,
  },
  stepContentNumberContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 100,
    width: STEP_CONTENT_NUMBER_SIZE,
    height: STEP_CONTENT_NUMBER_SIZE,
    backgroundColor: primitive.$bbqBrown,
  },
  stepContentNumber: {
    color: primitive.$white,
    fontFamily: primitive.fontFamily.$body,
    textAlign: 'center',
    fontSize: primitive.fontSizes.xs,
    lineHeight: primitive.fontSizes.xs,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
  },
  stepContentTitle: {
    fontSize: primitive.fontSizes.md,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
  },
  stepContentDescription: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
    fontSize: primitive.fontSizes.sm,
  },
  stepContentTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.$3,
  },
  stepContentDescriptionContainer: {
    flex: 1,
    marginLeft: STEP_CONTENT_NUMBER_SIZE + spacing.$3,
  },
});
